import { useState, useEffect, useRef } from 'react'
import { Play, Pause, SkipBack, SkipForward, Calendar, Clock, FileVideo, X, FastForward, Rewind, RotateCcw, RotateCw, ArrowLeft, RefreshCw, ChevronDown, Zap, Users } from 'lucide-react'
import Hls from 'hls.js'
import api from '../services/api'
import useSnapshotWithFallback from '../hooks/useSnapshotWithFallback'
import useStreamManager from '../hooks/useStreamManager'

const CameraPlayback = ({ camera, isOpen, onClose }) => {
    const [recordings, setRecordings] = useState([])
    const [selectedRecording, setSelectedRecording] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState(null)
    const [playbackStream, setPlaybackStream] = useState(null)
    const [isPlaying, setIsPlaying] = useState(false)
    const [currentTime, setCurrentTime] = useState(0)
    const [duration, setDuration] = useState(0)
    const [playbackId, setPlaybackId] = useState(null)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [playbackSpeed, setPlaybackSpeed] = useState(1)
    const [showSpeedControls, setShowSpeedControls] = useState(false)
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [showQuickActions, setShowQuickActions] = useState(false)
    const [viewerCount, setViewerCount] = useState(0)

    const videoRef = useRef(null)
    const hlsRef = useRef(null)
    
    // Stream manager for sharing integration
    const streamManager = useStreamManager()

    // Get camera snapshot for loading state
    const { src: snapshotSrc, loading: snapshotLoading } = useSnapshotWithFallback(camera?._id, 0, {
        gifTimeout: 8000,
        jpegTimeout: 5000,
        gifDuration: 2,
        gifFps: 8
    })

    useEffect(() => {
        if (isOpen && camera) {
            loadRecordings()
        }
    }, [isOpen, camera])

    useEffect(() => {
        if (playbackStream && videoRef.current) {
            initializePlaybackPlayer()
        }
    }, [playbackStream])

    // Listen for fullscreen changes
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, []);

    // Close controls when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showSpeedControls && !event.target.closest('.speed-controls')) {
                setShowSpeedControls(false);
            }
            if (showQuickActions && !event.target.closest('.quick-actions')) {
                setShowQuickActions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showSpeedControls, showQuickActions]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy()
            }
            if (playbackId) {
                stopPlayback()
            }
        }
    }, [])

    const loadRecordings = async () => {
        if (!camera) return

        setIsLoading(true)
        setError(null)
        try {
            const res = await api.get(`/cameras/${camera._id}/recordings`)
            setRecordings(res.data.data.recordings || [])
        } catch (error) {
            console.error('Error loading recordings:', error)
            setError('Failed to load recordings: ' + (error.response?.data?.message || error.message))
        } finally {
            setIsLoading(false)
        }
    }

    const startPlayback = async (recording) => {
        if (!recording) return

        setIsLoading(true)
        setError(null)
        
        try {
            const res = await api.get(`/cameras/${camera._id}/playback`, {
                params: {
                    startTime: recording.startTime,
                    endTime: recording.endTime,
                    recordingId: recording.id
                }
            })

            const streamUrl = res.data.data.streamUrl
            const playbackId = res.data.data.playbackId

            setPlaybackStream(streamUrl)
            setPlaybackId(playbackId)
            setSelectedRecording(recording)
            setIsPlaying(false)
            setCurrentTime(0)
            
            // Register playback stream with sharing integration
            streamManager.registerStream(camera._id, {
                url: streamUrl,
                type: 'playback',
                userId: 'current_user', // TODO: Get from auth context
                playbackId: playbackId
            });
        } catch (error) {
            console.error('Error starting playback:', error)
            setError('Failed to start playback: ' + (error.response?.data?.message || error.message))
        } finally {
            setIsLoading(false)
        }
    }

    const stopPlayback = async () => {
        if (playbackId) {
            try {
                await api.delete(`/cameras/${camera._id}/playback`, {
                    params: { playbackId }
                })
            } catch (error) {
                console.error('Error stopping playback:', error)
            }
        }

        setPlaybackStream(null)
        setPlaybackId(null)
        setSelectedRecording(null)
        setIsPlaying(false)
        setCurrentTime(0)
        setDuration(0)

        if (hlsRef.current) {
            hlsRef.current.destroy()
            hlsRef.current = null
        }
    }

    const refreshPlayback = async () => {
        if (!selectedRecording) return

        setIsRefreshing(true)
        setError(null)
        
        // Stop current playback first
        await stopPlayback()
        
        // Wait a moment for cleanup
        await new Promise(resolve => setTimeout(resolve, 500))
        
        // Restart playback
        await startPlayback(selectedRecording)
        
        setIsRefreshing(false)
    }

    const initializePlaybackPlayer = () => {
        if (!videoRef.current || !playbackStream) return

        const absUrl = getAbsoluteStreamUrl(playbackStream)
        console.log('Initializing playback player with URL:', absUrl)

        // Clean up existing HLS instance
        if (hlsRef.current) {
            hlsRef.current.destroy()
        }

        if (Hls.isSupported()) {
            const hls = new Hls({
                debug: false,
                enableWorker: true,
                lowLatencyMode: false, // Disable for playback
                backBufferLength: 90,
                maxBufferLength: 30,
                maxMaxBufferLength: 60,
                maxBufferSize: 60 * 1000 * 1000,
                maxBufferHole: 0.5,
                highBufferWatchdogPeriod: 2,
                nudgeOffset: 0.2,
                nudgeMaxRetry: 5,
                maxFragLookUpTolerance: 0.25,
                liveSyncDurationCount: 3,
                liveMaxLatencyDurationCount: 10,
                fragLoadingMaxRetry: 3,
                manifestLoadingMaxRetry: 3,
                levelLoadingMaxRetry: 3
            })

            hlsRef.current = hls

            hls.loadSource(absUrl)
            hls.attachMedia(videoRef.current)

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                console.log('Playback manifest loaded successfully')
                setDuration(videoRef.current.duration)
            })

            hls.on(Hls.Events.ERROR, (event, data) => {
                console.error('HLS Playback Error:', data)
                if (data.fatal) {
                    setError('Playback stream failed')
                }
            })

            videoRef.current.onloadedmetadata = () => {
                setDuration(videoRef.current.duration)
            }

            videoRef.current.ontimeupdate = () => {
                setCurrentTime(videoRef.current.currentTime)
            }
            
            videoRef.current.onplay = () => {
                // Update viewer count when playback starts
                const currentViewerCount = streamManager.getViewerCount(camera._id);
                setViewerCount(currentViewerCount);
            }

            videoRef.current.onended = () => {
                setIsPlaying(false)
            }
        } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
            videoRef.current.src = absUrl
            videoRef.current.onloadedmetadata = () => {
                setDuration(videoRef.current.duration)
            }
            videoRef.current.ontimeupdate = () => {
                setCurrentTime(videoRef.current.currentTime)
            }
        } else {
            setError('HLS not supported in this browser')
        }
    }

    const handlePlayPause = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause()
            } else {
                videoRef.current.play()
            }
            setIsPlaying(!isPlaying)
        }
    }

    const handleSeek = (time) => {
        if (videoRef.current) {
            videoRef.current.currentTime = time
            setCurrentTime(time)
        }
    }

    const handleSkip = (seconds) => {
        if (videoRef.current) {
            const newTime = Math.max(0, Math.min(videoRef.current.currentTime + seconds, duration))
            handleSeek(newTime)
        }
    }

    // Enhanced skip functions for better navigation
    const handleFastForward = () => {
        handleSkip(60) // Skip 1 minute forward
    }

    const handleRewind = () => {
        handleSkip(-60) // Skip 1 minute backward
    }

    const handleSkipToStart = () => {
        handleSeek(0)
    }

    const handleSkipToEnd = () => {
        handleSeek(duration)
    }

    // Playback speed control
    const handleSpeedChange = (speed) => {
        if (videoRef.current) {
            videoRef.current.playbackRate = speed
            setPlaybackSpeed(speed)
            setShowSpeedControls(false)
        }
    }

    const speedOptions = [0.5, 1, 1.5, 2, 3, 4]

    const formatTime = (seconds) => {
        const hours = Math.floor(seconds / 3600)
        const minutes = Math.floor((seconds % 3600) / 60)
        const secs = Math.floor(seconds % 60)
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString()
    }

    const getAbsoluteStreamUrl = (url) => {
        if (!url) return ''
        // If it's already an absolute URL (like ISAPI direct URLs), return as-is
        if (url.startsWith('http://') || url.startsWith('https://')) return url
        // For relative URLs (like local stream paths), prepend the base URL
        const baseUrl = import.meta.env.VITE_API_STREAM_URL || import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:3667'
        return baseUrl + url
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
            <div className={`bg-white rounded-xl shadow-2xl w-full ${isFullscreen ? 'max-w-full max-h-full' : 'max-w-7xl max-h-[95vh]'} overflow-hidden`}>
                {/* Compact Header */}
                <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={onClose}
                            className="flex items-center space-x-1 p-1.5 hover:bg-gray-200 rounded transition-colors text-gray-600 hover:text-gray-900"
                            title="Back to Camera List"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            <span className="text-xs font-medium">Back</span>
                        </button>
                        <div className="h-4 w-px bg-gray-300"></div>
                        <FileVideo className="h-4 w-4 text-blue-600" />
                        <div>
                            <h2 className="text-sm font-semibold text-gray-900">{camera?.name} - Playback</h2>
                        </div>
                        {/* Viewer Count */}
                        {viewerCount > 1 && (
                            <div className="flex items-center space-x-1 ml-2">
                                <Users className="h-3 w-3 text-blue-600" />
                                <span className="text-xs text-gray-600">{viewerCount} viewers</span>
                            </div>
                        )}
                    </div>
                    <div className="flex items-center space-x-1">
                        <button
                            onClick={loadRecordings}
                            disabled={isLoading}
                            className="flex items-center space-x-1 px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                            title="Refresh Recordings"
                        >
                            <RefreshCw className={`h-3 w-3 ${isLoading ? 'animate-spin' : ''}`} />
                            <span>Refresh</span>
                        </button>
                        <button
                            onClick={onClose}
                            className="p-1.5 hover:bg-gray-200 rounded transition-colors"
                            title="Close"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                </div>

                <div className={`flex ${isFullscreen ? 'h-[calc(100vh-80px)]' : 'h-[calc(95vh-80px)]'}`}>
                    {/* Compact Recordings List */}
                    <div className="w-80 border-r border-gray-200 overflow-y-auto bg-gray-50">
                        <div className="p-3">
                            <h3 className="text-sm font-semibold mb-3 flex items-center text-gray-700">
                                <Calendar className="h-4 w-4 mr-1.5" />
                                Recordings
                            </h3>

                            {isLoading ? (
                                <div className="text-center py-4">
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mx-auto mb-2"></div>
                                    <p className="text-xs text-gray-500">Loading...</p>
                                </div>
                            ) : error ? (
                                <div className="text-center py-4">
                                    <div className="text-red-500 mb-1 text-sm">⚠️</div>
                                    <p className="text-red-600 text-xs">{error}</p>
                                    <button
                                        onClick={loadRecordings}
                                        className="mt-2 bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-xs transition-colors"
                                    >
                                        Retry
                                    </button>
                                </div>
                            ) : recordings.length === 0 ? (
                                <div className="text-center py-4">
                                    <div className="text-gray-400 mb-1 text-sm">📹</div>
                                    <p className="text-gray-500 text-xs">No recordings</p>
                                </div>
                            ) : (
                                <div className="space-y-1.5">
                                    {recordings.map((recording) => (
                                        <div
                                            key={recording.id}
                                            className={`p-2 rounded border cursor-pointer transition-colors ${selectedRecording?.id === recording.id
                                                ? 'border-blue-500 bg-blue-50'
                                                : 'border-gray-200 hover:border-gray-300 hover:bg-white'
                                                }`}
                                            onClick={() => startPlayback(recording)}
                                        >
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-xs font-medium text-gray-900 truncate">
                                                    {recording.type === 'motion' ? 'Motion' : 'Continuous'}
                                                </span>
                                                <span className="text-xs text-gray-500">{recording.size}</span>
                                            </div>
                                            <div className="text-xs text-gray-600 space-y-0.5">
                                                <div className="truncate">
                                                    {new Date(recording.startTime).toLocaleString('en-US', {
                                                        month: 'short', day: 'numeric',
                                                        hour: '2-digit', minute: '2-digit'
                                                    })}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {Math.floor(recording.duration / 60)}m {recording.duration % 60}s
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Playback Video */}
                    <div className="flex-1 flex flex-col">
                        {selectedRecording ? (
                            <>
                                {/* Video Player */}
                                <div className="flex-1 bg-black relative">
                                    {playbackStream ? (
                                        <video
                                            ref={videoRef}
                                            className="w-full h-full bg-black"
                                            controls={false}
                                            onPlay={() => setIsPlaying(true)}
                                            onPause={() => setIsPlaying(false)}
                                        />
                                    ) : (
                                        <div className="flex items-center justify-center h-full text-white relative">
                                            {isLoading ? (
                                                <div className="text-center">
                                                    {/* Show camera snapshot/GIF while loading */}
                                                    {snapshotSrc && !snapshotLoading && (
                                                        <div className="mb-4">
                                                            <img 
                                                                src={snapshotSrc} 
                                                                alt="Camera snapshot" 
                                                                className="w-32 h-24 object-cover rounded-lg mx-auto"
                                                                style={{ imageRendering: 'auto' }}
                                                            />
                                                        </div>
                                                    )}
                                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
                                                    <p>Loading playback...</p>
                                                    {isRefreshing && <p className="text-sm text-gray-300 mt-2">Refreshing stream...</p>}
                                                </div>
                                            ) : error ? (
                                                <div className="text-center">
                                                    <div className="text-4xl mb-2">⚠️</div>
                                                    <p className="text-red-400">{error}</p>
                                                    {/* Show camera snapshot even on error */}
                                                    {snapshotSrc && !snapshotLoading && (
                                                        <div className="mt-4">
                                                            <img 
                                                                src={snapshotSrc} 
                                                                alt="Camera snapshot" 
                                                                className="w-32 h-24 object-cover rounded-lg mx-auto"
                                                                style={{ imageRendering: 'auto' }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="text-center">
                                                    {/* Show camera snapshot when no recording selected */}
                                                    {snapshotSrc && !snapshotLoading && (
                                                        <div className="mb-4">
                                                            <img 
                                                                src={snapshotSrc} 
                                                                alt="Camera snapshot" 
                                                                className="w-32 h-24 object-cover rounded-lg mx-auto"
                                                                style={{ imageRendering: 'auto' }}
                                                            />
                                                        </div>
                                                    )}
                                                    <div className="text-4xl mb-2">📹</div>
                                                    <p>Select a recording to start playback</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Compact Playback Controls */}
                                <div className={`p-3 bg-gray-50 border-t border-gray-200 ${isFullscreen ? 'min-h-[100px]' : ''}`}>
                                    {/* Main Controls Row */}
                                    <div className="flex items-center space-x-2 mb-3">
                                        {/* Play/Pause */}
                                        <button
                                            onClick={handlePlayPause}
                                            disabled={!playbackStream}
                                            className="p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded transition-colors"
                                            title="Play/Pause"
                                        >
                                            {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                                        </button>

                                        {/* Quick Actions Dropdown */}
                                        <div className="relative quick-actions">
                                            <button
                                                onClick={() => setShowQuickActions(!showQuickActions)}
                                                disabled={!playbackStream}
                                                className="flex items-center space-x-1 px-2 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white rounded transition-colors"
                                                title="Quick Actions"
                                            >
                                                <Zap className="h-4 w-4" />
                                                <ChevronDown className="h-3 w-3" />
                                            </button>
                                            
                                            {showQuickActions && (
                                                <div className="absolute bottom-full left-0 mb-2 bg-white border border-gray-300 rounded-lg shadow-lg z-10 min-w-40">
                                                    <div className="p-1">
                                                        <button
                                                            onClick={() => { handleSkip(-300); setShowQuickActions(false); }}
                                                            className="block w-full px-3 py-2 text-left hover:bg-gray-50 rounded text-sm text-gray-700"
                                                        >
                                                            ⏪ -5 min
                                                        </button>
                                                        <button
                                                            onClick={() => { handleSkip(-600); setShowQuickActions(false); }}
                                                            className="block w-full px-3 py-2 text-left hover:bg-gray-50 rounded text-sm text-gray-700"
                                                        >
                                                            ⏪ -10 min
                                                        </button>
                                                        <button
                                                            onClick={() => { handleSkip(-60); setShowQuickActions(false); }}
                                                            className="block w-full px-3 py-2 text-left hover:bg-gray-50 rounded text-sm text-gray-700"
                                                        >
                                                            ⏪ -1 min
                                                        </button>
                                                        <button
                                                            onClick={() => { handleSkip(-30); setShowQuickActions(false); }}
                                                            className="block w-full px-3 py-2 text-left hover:bg-gray-50 rounded text-sm text-gray-700"
                                                        >
                                                            ⏪ -30s
                                                        </button>
                                                        <div className="border-t border-gray-200 my-1"></div>
                                                        <button
                                                            onClick={() => { handleSkip(30); setShowQuickActions(false); }}
                                                            className="block w-full px-3 py-2 text-left hover:bg-gray-50 rounded text-sm text-gray-700"
                                                        >
                                                            ⏩ +30s
                                                        </button>
                                                        <button
                                                            onClick={() => { handleSkip(60); setShowQuickActions(false); }}
                                                            className="block w-full px-3 py-2 text-left hover:bg-gray-50 rounded text-sm text-gray-700"
                                                        >
                                                            ⏩ +1 min
                                                        </button>
                                                        <button
                                                            onClick={() => { handleSkip(300); setShowQuickActions(false); }}
                                                            className="block w-full px-3 py-2 text-left hover:bg-gray-50 rounded text-sm text-gray-700"
                                                        >
                                                            ⏩ +5 min
                                                        </button>
                                                        <button
                                                            onClick={() => { handleSkip(600); setShowQuickActions(false); }}
                                                            className="block w-full px-3 py-2 text-left hover:bg-gray-50 rounded text-sm text-gray-700"
                                                        >
                                                            ⏩ +10 min
                                                        </button>
                                                        <div className="border-t border-gray-200 my-1"></div>
                                                        <button
                                                            onClick={() => { handleSkipToStart(); setShowQuickActions(false); }}
                                                            className="block w-full px-3 py-2 text-left hover:bg-gray-50 rounded text-sm text-gray-700"
                                                        >
                                                            ⏮️ Start
                                                        </button>
                                                        <button
                                                            onClick={() => { handleSkipToEnd(); setShowQuickActions(false); }}
                                                            className="block w-full px-3 py-2 text-left hover:bg-gray-50 rounded text-sm text-gray-700"
                                                        >
                                                            ⏭️ End
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Refresh Stream */}
                                        <button
                                            onClick={refreshPlayback}
                                            disabled={!selectedRecording || isRefreshing}
                                            className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded transition-colors"
                                            title="Refresh Stream"
                                        >
                                            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                                        </button>

                                        {/* Compact Time Display */}
                                        <div className="flex-1 text-center">
                                            <div className="text-xs font-medium text-gray-700">
                                                {formatTime(currentTime)} / {formatTime(duration)} • {playbackSpeed}x
                                            </div>
                                        </div>

                                        {/* Speed Control */}
                                        <div className="relative speed-controls">
                                            <button
                                                onClick={() => setShowSpeedControls(!showSpeedControls)}
                                                disabled={!playbackStream}
                                                className="px-2 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white rounded transition-colors text-xs font-medium"
                                                title="Playback Speed"
                                            >
                                                {playbackSpeed}x
                                            </button>
                                            
                                            {showSpeedControls && (
                                                <div className="absolute bottom-full right-0 mb-2 bg-white border border-gray-300 rounded shadow-lg z-10">
                                                    {speedOptions.map((speed) => (
                                                        <button
                                                            key={speed}
                                                            onClick={() => handleSpeedChange(speed)}
                                                            className={`block w-full px-3 py-1.5 text-left hover:bg-gray-50 first:rounded-t last:rounded-b transition-colors text-sm ${
                                                                playbackSpeed === speed ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                                                            }`}
                                                        >
                                                            {speed}x
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* Stop Playback */}
                                        <button
                                            onClick={stopPlayback}
                                            className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors text-xs font-medium"
                                            title="Stop Playback"
                                        >
                                            Stop
                                        </button>
                                    </div>

                                    {/* Compact Timeline */}
                                    <div className="w-full mb-2">
                                        <input
                                            type="range"
                                            min="0"
                                            max={duration || 0}
                                            value={currentTime}
                                            onChange={(e) => handleSeek(parseFloat(e.target.value))}
                                            className={`w-full h-2 bg-gray-200 rounded appearance-none cursor-pointer slider`}
                                            disabled={!playbackStream}
                                            style={{
                                                background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(currentTime / (duration || 1)) * 100}%, #e5e7eb ${(currentTime / (duration || 1)) * 100}%, #e5e7eb 100%)`
                                            }}
                                        />
                                    </div>

                                    {/* Compact Recording Info */}
                                    {!isFullscreen && selectedRecording && (
                                        <div className="text-xs text-gray-600 text-center">
                                            <span className="font-medium">{selectedRecording.type === 'motion' ? 'Motion' : 'Continuous'}</span>
                                            <span className="mx-2">•</span>
                                            <span>{selectedRecording.size}</span>
                                            <span className="mx-2">•</span>
                                            <span>{new Date(selectedRecording.startTime).toLocaleString('en-US', {
                                                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                                            })}</span>
                                        </div>
                                    )}

                                    
                                </div>
                            </>
                        ) : (
                            <div className="flex-1 flex items-center justify-center text-gray-500">
                                <div className="text-center">
                                    <div className="text-6xl mb-4">📹</div>
                                    <h3 className="text-xl font-semibold mb-2">No Recording Selected</h3>
                                    <p>Choose a recording from the list to start playback</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default CameraPlayback 